import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { api } from '@aeon-karaoke/cloud-convex';
import type { Genre } from '@aeon-karaoke/cloud-convex/model/genres';
import { Button, MonoLabel, isHandleSubmittable, useHandleStatus } from '@/component';
import { useCurrentUser, type CurrentUser } from '@/hook';
import { convex } from '@/lib/convex';
import { toResult } from '@/lib/result-adapter';
import { cn } from '@/lib/cn';
import { WelcomeGenresStep } from './WelcomeGenresStep';
import { WelcomeIdentityStep } from './WelcomeIdentityStep';
import { WelcomeUploadStep } from './WelcomeUploadStep';

const STEPS = [1, 2, 3] as const;
type Step = (typeof STEPS)[number];

const isFreePlan = (status: CurrentUser['subscriptionStatus']) => status !== 'active' && status !== 'trialing';

/**
 * /welcome: the three-step first run. Steps 1 and 2 save through
 * users.updateProfile as they pass; Finish, Skip and Choose a file all stamp
 * users.completeOnboarding and then leave for where that choice points. The
 * page navigates itself because route guards only run on the way in: nothing
 * else moves the user when the row changes under them. `destination` is
 * where they were heading before the first run.
 */
export function WelcomePage({ destination }: { destination: string }) {
  const { user } = useCurrentUser();
  if (!user) return null;
  return <WelcomeFlow user={user} destination={destination} />;
}

function WelcomeFlow({ user, destination }: { user: CurrentUser; destination: string }) {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [name, setName] = useState(user.displayName ?? '');
  const [handle, setHandle] = useState(user.handle ?? '');
  const [genres, setGenres] = useState<Genre[]>(() => (user.genres ?? []) as Genre[]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleStatus = useHandleStatus(handle);

  async function save(call: () => Promise<null>, then: () => void) {
    setBusy(true);
    setError(null);
    const result = await toResult(call);
    setBusy(false);
    if (result.ok) then();
    else setError(result.error.message);
  }

  const finish = (leave: () => void) => save(() => convex.mutation(api.users.completeOnboarding, {}), leave);
  const toDestination = () => void navigate({ href: destination, replace: true });
  const toCreate = () => void navigate({ to: '/create', replace: true });

  function next() {
    if (step === 1) {
      void save(
        () => convex.mutation(api.users.updateProfile, { displayName: name, handle }),
        () => setStep(2)
      );
    } else if (step === 2) {
      void save(
        () => convex.mutation(api.users.updateProfile, { genres }),
        () => setStep(3)
      );
    } else {
      void finish(toDestination);
    }
  }

  const canContinue = step !== 1 || (name.trim().length > 0 && isHandleSubmittable(handleStatus));

  return (
    <div className="mx-auto max-w-[660px] px-8 pb-24 pt-[72px]">
      <div className="flex items-center gap-[11px]">
        <img src="/logo.svg" alt="" width={24} height={24} className="logo-spin-slow" />
        <MonoLabel className="tracking-[0.2em]">Step {step} of 3</MonoLabel>
      </div>
      <div className="mt-[22px] flex gap-2" aria-hidden="true">
        {STEPS.map((n) => (
          <div
            key={n}
            data-reached={step >= n}
            className={cn(
              'h-[3px] flex-1 rounded-sm transition-colors duration-300 motion-reduce:transition-none',
              step >= n ? 'bg-accFill' : 'bg-line'
            )}
          />
        ))}
      </div>

      {step === 1 && (
        <WelcomeIdentityStep
          user={user}
          name={name}
          handle={handle}
          onNameChange={setName}
          onHandleChange={setHandle}
        />
      )}
      {step === 2 && <WelcomeGenresStep selected={genres} onChange={setGenres} />}
      {step === 3 && (
        <WelcomeUploadStep isFree={isFreePlan(user.subscriptionStatus)} onChooseFile={() => void finish(toCreate)} />
      )}

      {error && <p className="mt-6 text-[13px] text-down">{error}</p>}

      <div className="mt-10 flex items-center gap-3 border-t border-line pt-6">
        <Button onClick={next} disabled={busy || !canContinue} className="px-[19px]">
          {step === 3 ? 'Finish' : 'Continue'}
        </Button>
        {step > 1 && (
          <Button variant="outline" disabled={busy} onClick={() => setStep(step === 3 ? 2 : 1)} className="px-[17px]">
            Back
          </Button>
        )}
        <Button variant="text" disabled={busy} onClick={() => void finish(toDestination)}>
          Skip for now
        </Button>
      </div>
    </div>
  );
}
